import React, { useContext, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import { AssetExportConfigurationContext } from '../../../../../../context/AssetExportConfigurationContext';
import {
  AssetExportConfiguration,
  AssetExportConfigurationField,
  AssetExportConfigurationInput,
} from '../../../../../../types/asset_export_configuration';
import AssetExportConfigurationForm, {
  DraggableAssetExportFieldIdentifier,
} from '../Common/AssetExportConfigurationForm';
import { TranslationLanguage } from '../../../../../../types/translation/translation';
import { ISO_LanguageCode } from '../../../../../../types/translation/localization.utils';
import { BackButton, ButtonVariant, CreateButton } from '../../../../../components/Button';
import { Routing } from '../../../../../../util/routes.util';
import { useRequest } from '../../../../../../requests/common/requestHook';
import { AssetExportConfigurationRequest } from '../../../../../../requests/assetExportConfiguration.request';
import { DraggableItemData } from '../../../../Common/DraggableItem';

interface CreateAssetExportConfigurationPaneProps {}

export default function CreateAssetExportConfigurationPane({}: CreateAssetExportConfigurationPaneProps) {
  const navigate = useNavigate();
  const { currentCompany } = useContext(CompanyContext);
  const { assetExportConfigurations, setAssetExportConfigurations, assetExportConfigurationsLoading } = useContext(
    AssetExportConfigurationContext,
  );

  const [input, setInput] = useState<AssetExportConfigurationInput>(
    new AssetExportConfigurationInput(
      AssetExportConfiguration.default(
        currentCompany.id,
        (currentCompany.settings.language || ISO_LanguageCode.EN) as TranslationLanguage,
      ),
    ),
  );

  const [create, { loading }] = useRequest(AssetExportConfigurationRequest.create);

  const handleCreate = async (
    configuration: AssetExportConfigurationInput,
    items: DraggableAssetExportFieldIdentifier[],
  ) => {
    configuration.mapping = DraggableItemData.toMap<AssetExportConfigurationField>(items);

    const result = await create({ data: [new AssetExportConfigurationInput(configuration).forCreate()] });
    if (!result) return;

    const config = result[0];
    assetExportConfigurations.set(config.id, new AssetExportConfiguration(config));
    setAssetExportConfigurations(new Map(assetExportConfigurations));
    navigate(Routing.assets.settings.report.overview());
  };

  const footer = (configuration: AssetExportConfigurationInput, items: DraggableAssetExportFieldIdentifier[]) => {
    return (
      <div className='flex justify-end'>
        <div className='flex items-center gap-2'>
          <BackButton
            disabled={loading}
            variant={ButtonVariant.secondary}
            onClick={() => navigate(Routing.assets.settings.report.overview())}
          />
          <CreateButton
            loading={loading}
            onClick={() => handleCreate(configuration, items)}
            disabled={!configuration.filename || !configuration.name}
          />
        </div>
      </div>
    );
  };

  return (
    <AssetExportConfigurationForm
      input={input}
      setInput={setInput}
      footer={footer}
      loading={assetExportConfigurationsLoading}
    />
  );
}
